/**
 * Outils d'audit des fiches joueurs : doublons exacts sur licenceId,
 * collisions après normalisation (variantes de chaîne).
 */

function uniquePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

export type PlayerDocRow = {
  docId: string;
  licenceId: string;
  name: string;
  surname: string;
  club: string;
  clubId: string;
  points: string;
  email?: string;
};

/** Clé stable pour comparer des numéros saisis différemment (espaces, séparateurs). */
export function normalizeLicenceKey(raw: string): string {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length > 0) {
    return digits.replace(/^0+/, "") || "0";
  }
  return trimmed.toLowerCase();
}

function identityFingerprint(p: PlayerDocRow): string {
  return [p.name, p.surname, p.club, p.points].join("|");
}

function normStr(s: string): string {
  return s.trim().toLowerCase();
}

/** Même personne côté identité (hors points). */
function sameIdentityExceptPoints(players: PlayerDocRow[]): boolean {
  if (players.length < 2) {
    return false;
  }
  const ref = players[0];
  const nName = normStr(ref.name);
  const nSurname = normStr(ref.surname);
  const nClub = normStr(ref.club);
  const nClubId = String(ref.clubId ?? "").trim();
  for (let i = 1; i < players.length; i += 1) {
    const p = players[i];
    if (
      normStr(p.name) !== nName ||
      normStr(p.surname) !== nSurname ||
      normStr(p.club) !== nClub ||
      String(p.clubId ?? "").trim() !== nClubId
    ) {
      return false;
    }
  }
  return true;
}

/** Identité alignée mais plusieurs valeurs de points (classement FFTT évolutif). */
function onlyPointsDifferAmongDuplicates(players: PlayerDocRow[]): boolean {
  if (!sameIdentityExceptPoints(players)) {
    return false;
  }
  const pointVals = uniquePreserveOrder(
    players.map((p) => String(p.points ?? "").trim())
  );
  return pointVals.length > 1;
}

export type ExactDuplicateGroup = {
  licenceId: string;
  players: PlayerDocRow[];
  hints: string[];
};

export type NormalizedCollisionGroup = {
  normalizedKey: string;
  distinctLicenceStrings: string[];
  players: PlayerDocRow[];
  hints: string[];
};

export type EmptyLicenceRow = {
  docId: string;
  label: string;
};

export type PlayerDuplicatesReport = {
  exactDuplicates: ExactDuplicateGroup[];
  normalizedCollisions: NormalizedCollisionGroup[];
  emptyOrMissingLicence: EmptyLicenceRow[];
};

function buildHintsForExactGroup(players: PlayerDocRow[]): string[] {
  const hints: string[] = [];
  const fingerprints = new Set(players.map(identityFingerprint));
  if (fingerprints.size === 1) {
    hints.push(
      "Toutes les fiches partagent le même nom, prénom, club et points — cause probable : condition de course sur findplayerfromlicence (deux créations quasi simultanées avant que la requête ne voie l’autre document), ou double soumission côté client."
    );
  } else if (onlyPointsDifferAmongDuplicates(players)) {
    hints.push(
      "Même licence, même nom / club : seuls les points FFTT diffèrent. C’est attendu dans le temps : le classement change, et chaque appel à findplayerfromlicence remplace les champs par la réponse courante de l’API. Une fiche peut donc rester « figée » à une ancienne valeur (ex. 1630 il y a longtemps) pendant qu’une autre a été rafraîchie plus récemment (ex. 1559 aujourd’hui). Ce n’est pas une saisie divergente."
    );
    hints.push(
      "Pour la fusion : en principe garder la fiche déjà reliée aux inscriptions du tournoi courant, mettre à jour l’autre ou la supprimer après avoir basculé les références (participation), puis un prochain passage par la recherche licence resynchronisera les points."
    );
  } else {
    hints.push(
      "Les données FFTT diffèrent entre les documents (identité ou club pas strictement identiques) — ancienne fiche, fusion manuelle, ou autre variante. À examiner avant fusion."
    );
  }

  const emails = players
    .map((p) => p.email?.trim())
    .filter((e): e is string => Boolean(e));
  const uniqueEmails = new Set(emails);
  if (emails.length > 0 && uniqueEmails.size === emails.length) {
    hints.push(
      "Emails distincts renseignés : les comptes utilisateurs peuvent pointer vers des fiches différentes."
    );
  }

  return hints;
}

function buildHintsForNormalizedGroup(
  distinctStrings: string[],
  players: PlayerDocRow[]
): string[] {
  const hints: string[] = [];
  if (distinctStrings.length > 1) {
    hints.push(
      "Plusieurs chaînes licenceId pour la même clé normalisée — cause probable : désalignement entre la valeur saisie à la recherche et la valeur renvoyée / stockée par la FFTT (espaces, zéros non significatifs, caractères non numériques)."
    );
  }
  const trimmedDiffers = distinctStrings.some(
    (s) => s !== s.trim() || /\s/.test(s)
  );
  if (trimmedDiffers) {
    hints.push(
      "Au moins une variante contient des espaces ou diffère après trim — normaliser côté serveur avant requête et avant écriture réduirait ce cas."
    );
  }
  const fingerprints = new Set(players.map(identityFingerprint));
  if (fingerprints.size === 1 && players.length > 1) {
    hints.push(
      "Profil FFTT identique sur toutes les fiches : fusion recommandée après vérification des inscriptions."
    );
  }
  return hints;
}

export function analyzePlayerDuplicates(rows: PlayerDocRow[]): PlayerDuplicatesReport {
  const exactDuplicates: ExactDuplicateGroup[] = [];
  const byExact = new Map<string, PlayerDocRow[]>();
  const emptyOrMissingLicence: EmptyLicenceRow[] = [];

  for (const row of rows) {
    const lid = row.licenceId;
    if (lid === undefined || lid === null || String(lid).trim() === "") {
      emptyOrMissingLicence.push({
        docId: row.docId,
        label: [row.surname, row.name].filter(Boolean).join(" ") || "(sans nom)",
      });
      continue;
    }
    const key = String(lid);
    const list = byExact.get(key);
    if (list) {
      list.push(row);
    } else {
      byExact.set(key, [row]);
    }
  }

  byExact.forEach((players, licenceId) => {
    if (players.length > 1) {
      exactDuplicates.push({
        licenceId,
        players,
        hints: buildHintsForExactGroup(players),
      });
    }
  });

  exactDuplicates.sort((a, b) => b.players.length - a.players.length);

  const byNorm = new Map<string, PlayerDocRow[]>();
  for (const row of rows) {
    const lid = row.licenceId;
    if (lid === undefined || lid === null || String(lid).trim() === "") {
      continue;
    }
    const nk = normalizeLicenceKey(String(lid));
    if (!nk) {
      continue;
    }
    const list = byNorm.get(nk);
    if (list) {
      list.push(row);
    } else {
      byNorm.set(nk, [row]);
    }
  }

  const normalizedCollisions: NormalizedCollisionGroup[] = [];
  byNorm.forEach((players, normalizedKey) => {
    const distinctIds = uniquePreserveOrder(players.map((p) => p.docId));
    const distinctStrings = uniquePreserveOrder(
      players.map((p) => String(p.licenceId))
    );
    if (distinctStrings.length > 1 && distinctIds.length >= 2) {
      normalizedCollisions.push({
        normalizedKey,
        distinctLicenceStrings: distinctStrings.sort(),
        players,
        hints: buildHintsForNormalizedGroup(distinctStrings, players),
      });
    }
  });

  normalizedCollisions.sort((a, b) => b.players.length - a.players.length);

  return {
    exactDuplicates,
    normalizedCollisions,
    emptyOrMissingLicence,
  };
}
